import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Ban, CheckCircle2, PackageCheck, Plus, Save, Send, Trash2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useStaff } from "@/context/StaffAuth";
import { api, errMsg, newKey } from "@/lib/api";
import { dateOnly, dateTime, money } from "@/lib/format";
import { ConfirmDialog, DemoBadge, ErrorState, Field, KeyValue, PageHeader, SectionCard, StatusBadge, rowCls, tdCls, thCls } from "@/components/admin/AdminUI";
import { VariantPicker } from "@/components/admin/VariantPicker";

const CURRENCIES = ["EUR", "USD", "GBP", "CHF", "JPY", "CNY", "HKD", "AED", "CAD", "AUD", "SEK", "DKK", "NOK"];
const blank = { supplier_id: "", warehouse_id: "", currency: "EUR", expected_delivery_date: "", supplier_reference: "", notes: "", items: [] };

const toForm = (po) => ({
  supplier_id: po.supplier_id, warehouse_id: po.warehouse_id, currency: po.currency || "EUR", expected_delivery_date: po.expected_delivery_date || "", supplier_reference: po.supplier_reference || "", notes: po.notes || "",
  items: (po.items || []).map((i) => ({ variant_id: i.variant_id, sku: i.sku, label: `${i.product?.brand_name || ""} ${i.product?.name || ""} · ${i.variant?.color || ""} / ${i.variant?.size || ""}`, quantity: String(i.quantity_ordered), unit_cost: i.unit_cost || "", supplier_sku: i.supplier_sku || "" })),
});

const lineTotal = (it) => {
  const q = Number(it.quantity || 0);
  const c = Number(it.unit_cost || 0);
  return Number.isFinite(q * c) ? q * c : 0;
};

export default function AdminPurchaseOrderDetail() {
  const { poId } = useParams();
  const isNew = poId === "new";
  const navigate = useNavigate();
  const { can } = useStaff();
  const canWrite = can("purchasing.write");
  const canReceive = can("purchasing.receive");

  const [po, setPo] = useState(null);
  const [form, setForm] = useState(blank);
  const [refs, setRefs] = useState({ suppliers: [], warehouses: [] });
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(isNew);
  const [confirm, setConfirm] = useState(null);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receipt, setReceipt] = useState({ lines: {}, note: "", delivery_reference: "", key: newKey() });

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/admin/purchase-orders/${poId}`);
      setPo(r.data);
      setForm(toForm(r.data));
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [poId, isNew]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    Promise.all([api.get("/admin/suppliers", { params: { page_size: 500 } }), api.get("/admin/warehouses", { params: { page_size: 200 } })])
      .then(([s, w]) => {
        setRefs({ suppliers: s.data.items || [], warehouses: w.data.items || [] });
        if (isNew) setForm((f) => ({ ...f, warehouse_id: f.warehouse_id || (w.data.items || []).find((x) => x.is_default)?.id || w.data.items?.[0]?.id || "" }));
      })
      .catch(() => {});
  }, [isNew]);

  useEffect(() => { document.title = `${isNew ? "New purchase order" : po?.po_number || "Purchase order"} · Admin`; }, [isNew, po]);

  const supplier = refs.suppliers.find((s) => s.id === form.supplier_id);
  useEffect(() => {
    if (isNew && supplier?.currency) setForm((f) => ({ ...f, currency: supplier.currency }));
  }, [supplier, isNew]);

  const total = useMemo(() => form.items.reduce((a, it) => a + lineTotal(it), 0), [form.items]);

  const addLine = (v) => {
    if (form.items.some((i) => i.variant_id === v.id)) return toast.error("Variant already on this order");
    setForm({ ...form, items: [...form.items, { variant_id: v.id, sku: v.sku, label: `${v.product?.brand_name || ""} ${v.product?.name || ""} · ${v.color} / ${v.size}`, quantity: "1", unit_cost: v.cost_price || "", supplier_sku: "" }] });
  };
  const setLine = (i, k, v) => setForm({ ...form, items: form.items.map((x, j) => (j === i ? { ...x, [k]: v } : x)) });

  const save = async () => {
    if (!form.supplier_id || !form.warehouse_id) return toast.error("Supplier and warehouse are required");
    if (!form.items.length) return toast.error("Add at least one line");
    setBusy(true);
    try {
      const body = { supplier_id: form.supplier_id, warehouse_id: form.warehouse_id, currency: form.currency, expected_delivery_date: form.expected_delivery_date || null, supplier_reference: form.supplier_reference, notes: form.notes, document_refs: [], items: form.items.map((i) => ({ variant_id: i.variant_id, quantity: Number(i.quantity), unit_cost: String(i.unit_cost).trim(), supplier_sku: i.supplier_sku })) };
      if (isNew) {
        const r = await api.post("/admin/purchase-orders", body);
        toast.success(`${r.data.po_number} created as draft`);
        navigate(`/admin/purchase-orders/${r.data.id}`, { replace: true });
      } else {
        const r = await api.put(`/admin/purchase-orders/${poId}`, body);
        setPo(r.data);
        setForm(toForm(r.data));
        setEditing(false);
        toast.success("Purchase order saved");
      }
    } catch (err) {
      toast.error(errMsg(err, "Could not save purchase order"));
    } finally {
      setBusy(false);
    }
  };

  const act = async (action) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/purchase-orders/${poId}/${action}`);
      setPo(r.data);
      setForm(toForm(r.data));
      toast.success({ submit: "Purchase order submitted to supplier", cancel: "Purchase order cancelled", close: "Purchase order closed; outstanding quantities cancelled" }[action]);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const openReceive = () => {
    const lines = {};
    (po.items || []).forEach((i) => { lines[i.id] = { quantity_received: String(i.outstanding), quantity_damaged: "0" }; });
    setReceipt({ lines, note: "", delivery_reference: "", key: newKey() });
    setReceiveOpen(true);
  };

  const submitReceipt = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const lines = Object.entries(receipt.lines).map(([item_id, l]) => ({ item_id, quantity_received: Number(l.quantity_received || 0), quantity_damaged: Number(l.quantity_damaged || 0) })).filter((l) => l.quantity_received > 0);
      const r = await api.post(`/admin/purchase-orders/${poId}/receive`, { idempotency_key: receipt.key, lines, note: receipt.note, delivery_reference: receipt.delivery_reference });
      setPo(r.data.purchase_order);
      setForm(toForm(r.data.purchase_order));
      toast.success(r.data.replay ? "Receipt already recorded (idempotent replay)" : `${r.data.receipt.receipt_number} posted — stock updated`);
      setReceiveOpen(false);
    } catch (err) {
      toast.error(errMsg(err, "Receipt rejected"));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (loading && !po) return <div className="space-y-6"><Skeleton className="h-12 w-80" /><Skeleton className="h-64 w-full" /></div>;

  const status = po?.status || "draft";
  const draft = status === "draft";
  const receivable = ["submitted", "partially_received"].includes(status);
  const editMode = isNew || (editing && draft);

  return (
    <div data-testid="admin-po-detail-page">
      <PageHeader
        eyebrow="Purchasing"
        title={isNew ? "New purchase order" : po.po_number}
        backTo="/admin/purchase-orders"
        backLabel="All purchase orders"
        description={!isNew && <span className="inline-flex flex-wrap items-center gap-2"><StatusBadge status={status} testId="po-status-badge" />{po.is_demo && <DemoBadge />}<span className="text-xs text-muted-foreground">Created {dateTime(po.created_at)} by {po.created_by}</span></span>}
        actions={(
          <>
            {!isNew && draft && canWrite && !editing && <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="rounded-lg" data-testid="po-edit-button">Edit draft</Button>}
            {!isNew && draft && canWrite && <Button variant="outline" size="sm" onClick={() => setConfirm("cancel")} className="rounded-lg" data-testid="po-cancel-button"><XCircle className="h-4 w-4 mr-1.5" />Cancel</Button>}
            {!isNew && draft && canWrite && !editing && <Button size="sm" variant="secondary" onClick={() => setConfirm("submit")} className="rounded-lg" data-testid="po-submit-button"><Send className="h-4 w-4 mr-1.5" />Submit</Button>}
            {!isNew && status === "submitted" && canWrite && <Button variant="outline" size="sm" onClick={() => setConfirm("cancel")} className="rounded-lg" data-testid="po-cancel-button"><XCircle className="h-4 w-4 mr-1.5" />Cancel</Button>}
            {!isNew && receivable && canWrite && <Button variant="outline" size="sm" onClick={() => setConfirm("close")} className="rounded-lg" data-testid="po-close-button"><Ban className="h-4 w-4 mr-1.5" />Close</Button>}
            {!isNew && receivable && canReceive && <Button size="sm" onClick={openReceive} className="rounded-lg" data-testid="po-receive-button"><PackageCheck className="h-4 w-4 mr-1.5" />Receive stock</Button>}
            {editMode && canWrite && <Button size="sm" onClick={save} disabled={busy} className="rounded-lg" data-testid="po-save-button"><Save className="h-4 w-4 mr-1.5" />{busy ? "Saving…" : isNew ? "Create draft" : "Save draft"}</Button>}
          </>
        )}
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <SectionCard title="Order lines" description={editMode ? "Costs are in the order currency. Each variant may appear once." : `${po?.units_received || 0} of ${po?.units_ordered || 0} units received.`} testId="po-lines-card">
            {editMode && canWrite && <div className="mb-4"><Field label="Add a variant"><VariantPicker value={null} onSelect={addLine} testId="po-variant-picker" exclude={form.items.map((i) => i.variant_id)} /></Field></div>}
            <div className="border rounded-xl overflow-hidden">
              <Table data-testid="po-lines-table">
                <TableHeader className="bg-secondary/60">
                  <TableRow>
                    <TableHead className={thCls}>SKU</TableHead>
                    <TableHead className={thCls}>Item</TableHead>
                    <TableHead className={`${thCls} text-right`}>Ordered</TableHead>
                    {!editMode && <TableHead className={`${thCls} text-right`}>Received</TableHead>}
                    {!editMode && <TableHead className={`${thCls} text-right`}>Outstanding</TableHead>}
                    <TableHead className={`${thCls} text-right`}>Unit cost</TableHead>
                    <TableHead className={`${thCls} text-right`}>Line total</TableHead>
                    {editMode && <TableHead className={thCls} />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {editMode ? (
                    form.items.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-10">No lines yet. Search a variant above to add it.</TableCell></TableRow> : form.items.map((it, i) => (
                      <TableRow key={it.variant_id} className={rowCls} data-testid="po-line-row">
                        <TableCell className={`${tdCls} font-mono text-xs`}>{it.sku}</TableCell>
                        <TableCell className={`${tdCls} text-sm`}>{it.label}<Input value={it.supplier_sku} onChange={(e) => setLine(i, "supplier_sku", e.target.value)} placeholder="Supplier SKU (optional)" className="h-7 mt-1 text-xs max-w-[220px]" aria-label="Supplier SKU" /></TableCell>
                        <TableCell className={`${tdCls} text-right`}><Input type="number" min={1} value={it.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} className="h-8 w-20 ml-auto text-right tabular" aria-label="Quantity" data-testid="po-line-qty-input" /></TableCell>
                        <TableCell className={`${tdCls} text-right`}><Input inputMode="decimal" value={it.unit_cost} onChange={(e) => setLine(i, "unit_cost", e.target.value)} placeholder="0.00" className="h-8 w-28 ml-auto text-right tabular" aria-label="Unit cost" data-testid="po-line-cost-input" /></TableCell>
                        <TableCell className={`${tdCls} text-right tabular font-medium`}>{money(lineTotal(it), form.currency)}</TableCell>
                        <TableCell className={tdCls}><Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setForm({ ...form, items: form.items.filter((_, j) => j !== i) })} aria-label="Remove line" data-testid="po-line-remove-button"><Trash2 className="h-4 w-4" /></Button></TableCell>
                      </TableRow>
                    ))
                  ) : (
                    (po?.items || []).map((it) => (
                      <TableRow key={it.id} className={rowCls} data-testid="po-line-row">
                        <TableCell className={`${tdCls} font-mono text-xs`}>{it.sku}{it.supplier_sku && <span className="block text-[10px] text-muted-foreground">Supplier {it.supplier_sku}</span>}</TableCell>
                        <TableCell className={tdCls}>{it.product?.brand_name} <span className="text-foreground/80">{it.product?.name}</span><span className="block text-xs text-muted-foreground">{it.variant?.color} / {it.variant?.size}</span></TableCell>
                        <TableCell className={`${tdCls} text-right tabular`}>{it.quantity_ordered}</TableCell>
                        <TableCell className={`${tdCls} text-right tabular`}>{it.quantity_received}{it.quantity_damaged > 0 && <span className="block text-[10px] text-[hsl(var(--accent-ochre))]">{it.quantity_damaged} damaged</span>}</TableCell>
                        <TableCell className={`${tdCls} text-right tabular ${it.outstanding > 0 ? "font-semibold" : "text-muted-foreground"}`}>{it.outstanding}{it.quantity_cancelled > 0 && <span className="block text-[10px] text-muted-foreground">{it.quantity_cancelled} cancelled</span>}</TableCell>
                        <TableCell className={`${tdCls} text-right tabular`}>{money(it.unit_cost, it.currency)}</TableCell>
                        <TableCell className={`${tdCls} text-right tabular font-medium`}>{money(it.line_total, it.currency)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-end mt-4"><p className="text-sm">Order total <span className="font-display text-2xl font-medium ml-3 tabular" data-testid="po-total">{money(editMode ? total : po?.total_cost, form.currency)}</span></p></div>
          </SectionCard>

          {!isNew && po?.receipts?.length > 0 && (
            <SectionCard title="Goods receipts" description="Every delivery posted against this order." testId="po-receipts-card">
              <ul className="divide-y">
                {po.receipts.map((r) => (
                  <li key={r.id} className="py-3 first:pt-0 last:pb-0" data-testid="po-receipt-item">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-semibold font-mono">{r.receipt_number} <StatusBadge status={r.status} className="ml-2" /></p>
                      <p className="text-xs text-muted-foreground">{dateTime(r.received_at)} · {r.received_by}{r.delivery_reference ? ` · ${r.delivery_reference}` : ""}</p>
                    </div>
                    <ul className="mt-1.5 text-xs text-muted-foreground space-y-0.5">{(r.lines || []).map((l) => <li key={l.item_id} className="font-mono">{l.sku}: +{l.quantity_received}{l.quantity_damaged ? ` (${l.quantity_damaged} damaged)` : ""}</li>)}</ul>
                    {r.note && <p className="text-xs mt-1">{r.note}</p>}
                  </li>
                ))}
              </ul>
            </SectionCard>
          )}
        </div>

        <div className="space-y-6">
          <SectionCard title="Order details" testId="po-details-card">
            {editMode ? (
              <div className="space-y-4">
                <Field label="Supplier" htmlFor="po-sup" required>
                  <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                    <SelectTrigger id="po-sup" data-testid="po-supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                    <SelectContent>{refs.suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.code})</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Deliver to warehouse" htmlFor="po-wh" required>
                  <Select value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })}>
                    <SelectTrigger id="po-wh" data-testid="po-warehouse-select"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                    <SelectContent>{refs.warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Currency" htmlFor="po-cur">
                    <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                      <SelectTrigger id="po-cur" data-testid="po-currency-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Expected delivery" htmlFor="po-eta"><Input id="po-eta" type="date" value={form.expected_delivery_date} onChange={(e) => setForm({ ...form, expected_delivery_date: e.target.value })} data-testid="po-eta-input" /></Field>
                </div>
                <Field label="Supplier reference" htmlFor="po-ref"><Input id="po-ref" value={form.supplier_reference} onChange={(e) => setForm({ ...form, supplier_reference: e.target.value })} data-testid="po-reference-input" /></Field>
                <Field label="Notes" htmlFor="po-notes"><Textarea id="po-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="po-notes-input" /></Field>
                {!isNew && <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setForm(toForm(po)); }}>Discard changes</Button>}
              </div>
            ) : (
              <KeyValue items={[["Supplier", `${po.supplier?.name || po.supplier_name} (${po.supplier?.code || ""})`], ["Warehouse", `${po.warehouse?.name || ""} (${po.warehouse_code})`], ["Currency", po.currency], ["Expected", po.expected_delivery_date ? dateOnly(po.expected_delivery_date) : "—"], ["Supplier ref", po.supplier_reference], ["Units", `${po.units_received} / ${po.units_ordered}`], ["Submitted", po.submitted_at ? dateTime(po.submitted_at) : "—"], ["Last receipt", po.last_received_at ? dateTime(po.last_received_at) : "—"], po.notes && ["Notes", po.notes]]} />
            )}
          </SectionCard>
          {!isNew && (
            <SectionCard title="Workflow">
              <ol className="space-y-2 text-sm">
                {["draft", "submitted", "partially_received", "received"].map((s, i) => {
                  const order = ["draft", "submitted", "partially_received", "received", "closed"];
                  const done = order.indexOf(status) >= i || status === "closed";
                  return <li key={s} className={`flex items-center gap-2 ${done ? "" : "text-muted-foreground"}`}><CheckCircle2 className={`h-4 w-4 ${done ? "text-[hsl(var(--accent-forest))]" : "text-border"}`} />{s === "partially_received" ? "Partially received" : s.charAt(0).toUpperCase() + s.slice(1)}</li>;
                })}
                {["closed", "cancelled"].includes(status) && <li className="flex items-center gap-2 text-muted-foreground"><Ban className="h-4 w-4" />{status === "closed" ? "Closed" : "Cancelled"}</li>}
              </ol>
            </SectionCard>
          )}
        </div>
      </div>

      <Dialog open={receiveOpen} onOpenChange={setReceiveOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="po-receive-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-medium">Receive stock</DialogTitle>
            <DialogDescription>Enter what physically arrived. Damaged units are received but held as unsellable. Over-receipt is rejected; the request is idempotent.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitReceipt} className="space-y-4 pt-2">
            <div className="border rounded-xl overflow-hidden">
              <Table>
                <TableHeader className="bg-secondary/60"><TableRow><TableHead className={thCls}>SKU</TableHead><TableHead className={`${thCls} text-right`}>Outstanding</TableHead><TableHead className={`${thCls} text-right`}>Receiving</TableHead><TableHead className={`${thCls} text-right`}>Of which damaged</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(po?.items || []).map((it) => (
                    <TableRow key={it.id} data-testid="po-receive-line">
                      <TableCell className={`${tdCls} font-mono text-xs`}>{it.sku}<span className="block text-[10px] text-muted-foreground font-sans">{it.variant?.color} / {it.variant?.size}</span></TableCell>
                      <TableCell className={`${tdCls} text-right tabular text-muted-foreground`}>{it.outstanding}</TableCell>
                      <TableCell className={`${tdCls} text-right`}><Input type="number" min={0} max={it.outstanding} disabled={it.outstanding === 0} value={receipt.lines[it.id]?.quantity_received ?? "0"} onChange={(e) => setReceipt({ ...receipt, lines: { ...receipt.lines, [it.id]: { ...receipt.lines[it.id], quantity_received: e.target.value } } })} className="h-8 w-24 ml-auto text-right tabular" aria-label="Quantity received" data-testid="po-receive-qty-input" /></TableCell>
                      <TableCell className={`${tdCls} text-right`}><Input type="number" min={0} disabled={it.outstanding === 0} value={receipt.lines[it.id]?.quantity_damaged ?? "0"} onChange={(e) => setReceipt({ ...receipt, lines: { ...receipt.lines, [it.id]: { ...receipt.lines[it.id], quantity_damaged: e.target.value } } })} className="h-8 w-24 ml-auto text-right tabular" aria-label="Quantity damaged" data-testid="po-receive-damaged-input" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Delivery reference" htmlFor="rc-ref"><Input id="rc-ref" value={receipt.delivery_reference} onChange={(e) => setReceipt({ ...receipt, delivery_reference: e.target.value })} placeholder="Carrier / packing slip" data-testid="po-receive-reference-input" /></Field>
              <Field label="Note" htmlFor="rc-note"><Input id="rc-note" value={receipt.note} onChange={(e) => setReceipt({ ...receipt, note: e.target.value })} data-testid="po-receive-note-input" /></Field>
            </div>
            <p className="text-[11px] text-muted-foreground font-mono">Idempotency key {receipt.key}</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setReceiveOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} data-testid="po-receive-submit-button"><Plus className="h-4 w-4 mr-1.5" />{busy ? "Posting…" : "Post receipt"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={{ submit: "Submit this purchase order?", cancel: "Cancel this purchase order?", close: "Close this purchase order?" }[confirm] || ""}
        description={{ submit: "Once submitted the order is locked for editing and becomes receivable. Expected units appear as incoming stock.", cancel: "Only orders without receipts can be cancelled. This cannot be undone.", close: "Outstanding quantities are cancelled and the order is finalised with what was received." }[confirm] || ""}
        confirmLabel={{ submit: "Submit order", cancel: "Cancel order", close: "Close order" }[confirm] || "Confirm"}
        destructive={confirm === "cancel"}
        busy={busy}
        onConfirm={() => act(confirm)}
      />
    </div>
  );
}
